import type {
  OrgModelEntity,
  OrgModelProjection,
  OrgModelRelationship,
  PositionComputedState,
} from '../models/OrgModelProjection';
import type { PositionStructureIntent } from '../models/PositionStructureIntent';

/**
 * Builds a disposable, pane-local projection while the canonical DB-owned
 * change-set settles. It never writes to Yjs or becomes source authority.
 */
export function applyOptimisticPositionStructure(
  model: OrgModelProjection,
  intent: PositionStructureIntent,
): OrgModelProjection {
  if (intent.mode !== 'live') return model;
  // retire_person is scenario-only (person facts need the organization_plan
  // change-set variant), so a live pane never has anything to project for it.
  if (intent.kind === 'retire_person') return model;
  if (intent.kind === 'update_person') {
    return {
      ...model,
      people: model.people.map((person) => sameRef(person, intent.personId)
        ? {
            ...person,
            label: intent.fields.name ?? person.label,
            props: { ...person.props, ...intent.fields },
          }
        : person),
    };
  }
  if (intent.kind === 'create') {
    return addPosition(model, intent, {
      label: intent.title,
      props: compactProps({
        name: intent.title,
        title: intent.title,
        department: intent.department,
        level: intent.level,
      }),
      managerPositionId: intent.managerPositionId ?? null,
    });
  }
  if (intent.kind === 'add_seat' || intent.kind === 'duplicate') {
    const source = findPosition(model, intent.sourcePositionId);
    if (!source) return model;
    const managerPositionId = managerFor(model, source.id);
    const label = intent.kind === 'duplicate'
      ? intent.title ?? `${source.label} copy`
      : source.label;
    return addPosition(model, intent, {
      label,
      props: intent.kind === 'add_seat'
        ? { ...source.props, name: label, title: label }
        : compactProps({
            name: label,
            title: label,
            department: readString(source.props.department),
            level: readString(source.props.level ?? source.props.managementLevel),
          }),
      managerPositionId,
      provenance: source.provenance,
    });
  }
  if (intent.kind === 'edit') {
    return {
      ...model,
      positions: model.positions.map((position) => sameRef(position, intent.positionId)
        ? {
            ...position,
            label: intent.title ?? position.label,
            props: {
              ...position.props,
              ...compactProps({
                name: intent.title,
                title: intent.title,
                department: intent.department,
                level: intent.level,
              }),
            },
          }
        : position),
    };
  }
  if (intent.kind === 'reparent') {
    const position = findPosition(model, intent.positionId);
    if (!position) return model;
    const relationships = model.relationships.filter((relationship) =>
      relationship.predicate !== 'reports_to' || !sameSubject(relationship, position));
    if (intent.managerPositionId) {
      const manager = findPosition(model, intent.managerPositionId);
      if (manager) relationships.push(optimisticReportingRelationship(intent, position.id, manager.id));
    }
    return {
      ...model,
      relationships,
      positionState: model.positionState.map((state) => state.positionId === position.id
        ? { ...state, managerPositionId: intent.managerPositionId ?? null }
        : state),
    };
  }
  if (intent.kind === 'add_secondary_reporting') {
    const position = findPosition(model, intent.positionId);
    const manager = findPosition(model, intent.managerPositionId);
    if (!position || !manager) return model;
    return {
      ...model,
      relationships: [...model.relationships, {
        ...optimisticReportingRelationship(intent, position.id, manager.id),
        id: `atlas-matrix-reports:${intent.intentId}:${position.id}`,
        externalId: `atlas-matrix-reports:${intent.intentId}`,
        predicate: 'matrix_reports_to',
      }],
    };
  }

  const retired = findPosition(model, intent.positionId);
  if (!retired) return model;
  const retiredManagerId = managerFor(model, retired.id);
  const relationships = model.relationships.flatMap((relationship) => {
    if (
      relationship.subjectRef === retired.id
      || relationship.objectRef === retired.id
      || relationship.subjectRef === retired.externalId
      || relationship.objectRef === retired.externalId
    ) {
      if (
        intent.reportingPolicy === 'reparent_direct_reports'
        && retiredManagerId
        && relationship.predicate === 'reports_to'
        && relationship.objectRef === retired.id
      ) {
        return [optimisticReportingRelationship(intent, relationship.subjectRef, retiredManagerId)];
      }
      return [];
    }
    return [relationship];
  });
  return {
    ...model,
    positions: model.positions.filter((position) => position.id !== retired.id),
    positionState: model.positionState.filter((state) => state.positionId !== retired.id),
    relationships,
  };
}

/**
 * Confirms that the canonical read model contains the semantic outcome of one
 * structure intent. A graph-version advance alone is insufficient: protected
 * assertions and their effective projection can settle on separate reads.
 */
export function positionStructureIntentMatchesModel(
  model: Pick<OrgModelProjection, 'people' | 'positions' | 'relationships'>,
  intent: PositionStructureIntent,
): boolean {
  if (intent.mode !== 'live') return true;
  // Scenario-only: a live intent never carries retire_person, and this
  // position-scoped model has no people to check against.
  if (intent.kind === 'retire_person') return true;
  if (intent.kind === 'update_person') {
    const person = model.people.find((candidate) => sameRef(candidate, intent.personId));
    return Boolean(person && Object.entries(intent.fields).every(([field, value]) =>
      person.props[field] === value));
  }
  if (intent.kind === 'create' || intent.kind === 'add_seat' || intent.kind === 'duplicate') {
    return Boolean(findPosition(model, `atlas-position:${intent.intentId}`));
  }
  if (intent.kind === 'retire') {
    return !findPosition(model, intent.positionId);
  }
  const position = findPosition(model, intent.positionId);
  if (!position) return false;
  if (intent.kind === 'edit') {
    return (intent.title === undefined || position.label === intent.title)
      && (intent.department === undefined
        || readString(position.props.department) === intent.department)
      && (intent.level === undefined
        || readString(position.props.level ?? position.props.managementLevel) === intent.level);
  }
  if (intent.kind === 'add_secondary_reporting') {
    const manager = findPosition(model, intent.managerPositionId);
    return Boolean(manager && model.relationships.some((relationship) =>
      relationship.predicate === 'matrix_reports_to'
        && sameSubject(relationship, position)
        && sameRef(manager, relationship.objectRef),
    ));
  }
  const manager = model.relationships.find((relationship) =>
    relationship.predicate === 'reports_to' && sameSubject(relationship, position));
  if (intent.managerPositionId === null) return manager === undefined;
  const expectedManager = findPosition(model, intent.managerPositionId);
  return Boolean(expectedManager && manager && sameRef(expectedManager, manager.objectRef));
}

function addPosition(
  model: OrgModelProjection,
  intent: PositionStructureIntent,
  input: {
    readonly label: string;
    readonly props: Readonly<Record<string, unknown>>;
    readonly managerPositionId: string | null;
    readonly provenance?: OrgModelEntity['provenance'];
  },
): OrgModelProjection {
  const id = `atlas-position:${intent.intentId}`;
  if (model.positions.some((position) => position.id === id || position.externalId === id)) {
    return model;
  }
  const position: OrgModelEntity = {
    id,
    externalId: id,
    kind: 'position',
    label: input.label,
    props: input.props,
    provenance: input.provenance ?? {
      sourceId: null,
      sourceBinding: null,
      source: null,
    },
  };
  const state: PositionComputedState = {
    positionId: id,
    managerPositionId: input.managerPositionId,
    occupantPersonIds: [],
    occupants: [],
    primaryOccupantPersonId: null,
    occupancyVisibility: model.occupancyVisibility,
    staffingStatus: model.occupancyVisibility === 'visible' ? 'vacant' : 'unknown',
    vacant: model.occupancyVisibility === 'visible' ? true : null,
    successorCount: 0,
  };
  return {
    ...model,
    positions: [...model.positions, position],
    positionState: [...model.positionState, state],
    relationships: input.managerPositionId
      ? [...model.relationships, optimisticReportingRelationship(intent, id, input.managerPositionId)]
      : model.relationships,
  };
}

function optimisticReportingRelationship(
  intent: PositionStructureIntent,
  subjectRef: string,
  objectRef: string,
): OrgModelRelationship {
  return {
    id: `atlas-reports:${intent.intentId}:${subjectRef}`,
    externalId: `atlas-reports:${intent.intentId}`,
    predicate: 'reports_to',
    subjectRef,
    objectRef,
    qualifiers: {},
    validFrom: intent.effectiveAt ?? null,
    validTo: null,
  };
}

function findPosition(
  model: Pick<OrgModelProjection, 'positions'>,
  ref: string,
): OrgModelEntity | undefined {
  return model.positions.find((position) => sameRef(position, ref));
}

function managerFor(
  model: Pick<OrgModelProjection, 'positions' | 'positionState' | 'relationships'>,
  positionId: string,
): string | null {
  const state = model.positionState.find((candidate) => candidate.positionId === positionId);
  if (state?.managerPositionId) return state.managerPositionId;
  return model.relationships.find((relationship) =>
    relationship.predicate === 'reports_to' && relationship.subjectRef === positionId)?.objectRef ?? null;
}

function sameRef(position: OrgModelEntity, ref: string): boolean {
  return position.id === ref
    || position.externalId === ref
    || `record:${position.id}` === ref
    || `record:${position.externalId}` === ref;
}

function sameSubject(relationship: OrgModelRelationship, position: OrgModelEntity): boolean {
  return relationship.subjectRef === position.id
    || relationship.subjectRef === position.externalId
    || relationship.subjectRef === `record:${position.id}`
    || relationship.subjectRef === `record:${position.externalId}`;
}

function compactProps(input: Record<string, string | undefined>): Record<string, string> {
  return Object.fromEntries(
    Object.entries(input)
      .filter((entry): entry is [string, string] => Boolean(entry[1]?.trim()))
      .map(([key, value]) => [key, value.trim()]),
  );
}

function readString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value.trim() : undefined;
}
